// ══════════════════════════════════════════════════════════
// SISTEMA DE TORNEO - Gestión de rondas, brackets y avance
// ══════════════════════════════════════════════════════════

import { PlayerSelection } from '../scenes/SelectionScene';
import { TorneoConfig } from '../scenes/MenuScene';

export interface MatchResult {
  playerIndex: number;
  className: string;
  specialName: string;
  teamId: number;
  status: 'alive' | 'eliminated';
  points: number;
  hp: number;
}

export interface MatchData {
  round: number;
  matchIndex: number;
  participants: number[];  // índices de jugadores en esta match
  winnerIndex: number | null; // índice del ganador (global)
  played: boolean;
}

export class TorneoSystem {
  public config: TorneoConfig;
  public selections: PlayerSelection[];
  public currentRound: number = 0;
  public rounds: MatchData[][] = []; // rounds[round] = matches[]
  public results: Map<number, MatchResult[]> = new Map(); // playerIndex -> results de cada partida
  public eliminatedPlayers: Set<number> = new Set();

  constructor(config: TorneoConfig, selections: PlayerSelection[]) {
    this.config = config;
    this.selections = selections;
    this.generateBracket();
  }

  private generateBracket(): void {
    const playerCount = this.selections.length;

    if (this.config.modo === 'equipos') {
      // Torneo por equipos: cada equipo se enfrenta una vez
      const teamCount = 2; // fijo 2 equipos por ahora
      this.rounds = [[{
        round: 0,
        matchIndex: 0,
        participants: Array.from({ length: playerCount }, (_, i) => i),
        winnerIndex: null,
        played: false,
      }]];
    } else {
      // Torneo individual: eliminatorias directas
      const shuffled = this.shuffleArray(Array.from({ length: playerCount }, (_, i) => i));
      let roundMatches: MatchData[] = [];
      for (let i = 0; i < shuffled.length; i += 2) {
        if (i + 1 < shuffled.length) {
          roundMatches.push({
            round: 0,
            matchIndex: roundMatches.length,
            participants: [shuffled[i], shuffled[i + 1]],
            winnerIndex: null,
            played: false,
          });
        } else {
          // Bye: pasa directamente
          roundMatches.push({
            round: 0,
            matchIndex: roundMatches.length,
            participants: [shuffled[i]],
            winnerIndex: shuffled[i],
            played: true,
          });
        }
      }
      this.rounds.push(roundMatches);
    }
  }

  /** Devuelve la siguiente partida sin jugar */
  getNextMatch(): MatchData | null {
    for (const round of this.rounds) {
      for (const match of round) {
        if (!match.played) return match;
      }
    }
    return null;
  }

  /** Devuelve los índices de los participantes activos (no eliminados) */
  getActivePlayers(): number[] {
    const active: number[] = [];
    for (let i = 0; i < this.selections.length; i++) {
      if (!this.eliminatedPlayers.has(i)) {
        active.push(i);
      }
    }
    return active;
  }

  /** Obtiene el número de ronda global actual */
  getCurrentRoundNumber(): number {
    return this.currentRound;
  }

  /** Registra el resultado de una partida */
  registerMatchResult(match: MatchData, matchResults: MatchResult[]): void {
    match.played = true;

    // Guardar resultados
    matchResults.forEach((r) => {
      const existing = this.results.get(r.playerIndex) || [];
      existing.push(r);
      this.results.set(r.playerIndex, existing);
      if (r.status === 'eliminated') {
        this.eliminatedPlayers.add(r.playerIndex);
      }
    });

    if (this.config.modo === 'equipos') {
      // En equipos: gana el equipo con más puntos/HP
      const teamScores = new Map<number, number>();
      matchResults.forEach((r) => {
        const current = teamScores.get(r.teamId) || 0;
        if (this.config.modoJuego === 'dopamina') {
          teamScores.set(r.teamId, current + r.points);
        } else {
          teamScores.set(r.teamId, current + r.hp);
        }
      });
      let bestTeam = -1;
      let bestScore = -1;
      teamScores.forEach((score, team) => {
        if (score > bestScore) { bestScore = score; bestTeam = team; }
      });
      // Marcar ganador: todos los miembros del equipo ganador
      const winnerPlayer = matchResults.find((r) => r.teamId === bestTeam);
      if (winnerPlayer) {
        match.winnerIndex = winnerPlayer.playerIndex;
      }
    } else {
      // Individual: gana el último en pie o más puntos
      const alive = matchResults.filter((r) => r.status === 'alive');
      if (alive.length === 1) {
        match.winnerIndex = alive[0].playerIndex;
      } else if (this.config.modoJuego === 'dopamina') {
        let best = matchResults[0];
        matchResults.forEach((r) => {
          if (r.points > best.points) best = r;
        });
        match.winnerIndex = best.playerIndex;
      } else {
        let best = matchResults[0];
        matchResults.forEach((r) => {
          if (r.hp > best.hp) best = r;
        });
        match.winnerIndex = best.playerIndex;
      }
    }

    // Generar siguiente ronda si es necesario
    this.generateNextRound();
  }

  private generateNextRound(): void {
    if (this.isTournamentOver()) return;

    const lastRound = this.rounds[this.rounds.length - 1];
    const allPlayed = lastRound.every((m) => m.played);
    if (!allPlayed) return;

    // Recoger ganadores para la siguiente ronda
    const winners: number[] = [];
    lastRound.forEach((m) => {
      if (m.winnerIndex !== null && !this.eliminatedPlayers.has(m.winnerIndex)) {
        winners.push(m.winnerIndex);
      } else if (m.participants.length === 1) {
        // Bye
        winners.push(m.participants[0]);
      }
    });

    if (winners.length <= 1) return; // torneo terminado

    const nextRound = this.rounds.length;
    const nextMatches: MatchData[] = [];
    for (let i = 0; i < winners.length; i += 2) {
      if (i + 1 < winners.length) {
        nextMatches.push({
          round: nextRound,
          matchIndex: nextMatches.length,
          participants: [winners[i], winners[i + 1]],
          winnerIndex: null,
          played: false,
        });
      } else {
        // Bye
        nextMatches.push({
          round: nextRound,
          matchIndex: nextMatches.length,
          participants: [winners[i]],
          winnerIndex: winners[i],
          played: true,
        });
      }
    }
    this.rounds.push(nextMatches);
  }

  isTournamentOver(): boolean {
    if (this.rounds.length === 0) return true;
    const lastRound = this.rounds[this.rounds.length - 1];
    const allPlayed = lastRound.every((m) => m.played);
    if (!allPlayed) return false;
    const winners = lastRound
      .filter((m) => m.winnerIndex !== null)
      .map((m) => m.winnerIndex!);
    return winners.length <= 1;
  }

  getChampion(): { playerIndex: number; selection: PlayerSelection } | null {
    if (!this.isTournamentOver()) return null;
    const lastRound = this.rounds[this.rounds.length - 1];
    for (const match of lastRound) {
      if (match.winnerIndex !== null) {
        const sel = this.selections[match.winnerIndex];
        if (sel) return { playerIndex: match.winnerIndex, selection: sel };
      }
    }
    return null;
  }

  /** Nombres de las rondas */
  getRoundName(round: number): string {
    const totalRounds = this.rounds.length;
    if (totalRounds === 1) return '🏆 FINAL';
    if (round === totalRounds - 1) return '🏆 FINAL';
    if (round === totalRounds - 2) return '⚔️ SEMIFINAL';
    if (round === totalRounds - 3) return '⚔️ CUARTOS';
    if (round === totalRounds - 4) return '⚔️ OCTAVOS';
    return `⚔️ RONDA ${round + 1}`;
  }

  private shuffleArray<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }
}
